import React, { useContext, useEffect, useState } from 'react'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { useGetItemsToSyncQuery, useMarkItemsAsCompletedMutation, useSyncItemsMutation } from 'services/ledger'
import LedgerSyncGrid from 'components/generic/datagrid/LedgerSyncGrid'
import AlertSnackbar from 'components/generic/snackbars'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import ActionBar from './components/ActionBar'
import { alertTypes, createSplitPaymentMap, mapToSyncObject, syncObjectTypes } from './utils'

const SyncContainer = () => {
  const context = useContext(ContentLayoutContext)
  const [alert, setAlert] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoadingGrid, setLoadingGrid] = useState(true)
  const { isAdmin } = useSelector(state => state.permissions)
  const { erpType } = useSelector(state => state.ledger || {})
  const ledgerSyncApiRef = useGridApiRef()

  const { data: dataItems, isLoading: isLoadingItems } = useGetItemsToSyncQuery(
    { syncStatus: 'notSynced' },
    { refetchOnMountOrArgChange: true },
  )
  const [syncItems, { isLoading: isSyncingItems }] = useSyncItemsMutation()
  const [transformedRows, setTransformedRows] = useState([])

  const [markAsSynced, { isLoading: isLoadingMarkAsSynced }] = useMarkItemsAsCompletedMutation()
  const [selectedRowData, setSelectedRowData] = useState([])
  const [isSyncing, setSyncing] = useState(false)
  const [lastSyncDate, setLastSyncDate] = useState(null)
  const [allowSync, setAllowSync] = useState(false)
  const summaryItems = [
    {
      label: 'UNPAID TICKET VALUE',
      value: '',
    },
    {
      label: 'PAYMENT VALUE',
      value: '',
    },
    {
      label: 'RECEIVABLES VALUE',
      value: '',
    },
  ]

  useEffect(() => {
    context.setContainerTitle('Ledger')
  }, [])

  useEffect(() => {
    if (dataItems) {
      const dataList = dataItems?.Items
      const splitPaymentHashMap = createSplitPaymentMap(dataList)
      const mapped = dataList
        .slice()
        .sort((a, b) => new Date(b.data?.date) - new Date(a.data?.date))
        .map(item => mapToSyncObject(item, splitPaymentHashMap))
      setTransformedRows(mapped)
    }
  }, [dataItems])

  const handleSelection = ids => {
    const selectedIDs = new Set(ids)
    // remove any duplicates
    const selectedRows = transformedRows.filter(row => selectedIDs.has(row.id.toString()))
    setSelectedRowData(selectedRows)
    setAllowSync(isAdmin && selectedIDs.size > 0 && !!erpType)
  }

  const buildPayload = selectedItems =>
    selectedItems.reduce((acc, cur) => {
      /**
       * the init sync endpoint doesn't distinguish IN_YARD_PAYMENT and IN_YARD_BILL object types
       * as far is its concerned, they are just payments and bills, so we transform here
       */
      let adjustedObjectType = cur.objectTypeId
      if (cur.objectTypeId === syncObjectTypes.IN_YARD_PAYMENT.id) {
        adjustedObjectType = syncObjectTypes.PAYMENT.id
      }
      if (
        cur.objectTypeId === syncObjectTypes.IN_YARD_BILL.id ||
        cur.objectTypeId === syncObjectTypes.FREIGHT_BILL.id
      ) {
        adjustedObjectType = syncObjectTypes.BILL.id
      }
      // find if we already have an item in the payload with the same object type
      const payloadItem = acc.find(p => p.objectType === adjustedObjectType)
      if (payloadItem) {
        payloadItem.ids.push(cur.itemId)
      } else {
        acc.push({
          objectType: adjustedObjectType,
          ids: [cur.itemId],
        })
      }
      return acc
    }, [])

  const handleSync = async () => {
    if (isAdmin && !!erpType) {
      setAlert('SYNC_IN_PROGRESS')
      setIsOpen(true)
      setSyncing(true)
      for (let idx = 0; idx < selectedRowData.length; idx++) {
        ledgerSyncApiRef.current.updateRows([
          {
            id: selectedRowData[idx].id,
            _action: 'delete',
          },
        ])
      }
      const syncPayload = buildPayload(selectedRowData)
      await syncItems({
        erpProvider: erpType,
        body: syncPayload,
      })
        .unwrap()
        .then(result => {
          setLastSyncDate(result[0]?.startDate)
          setAllowSync(false)
        })
        .catch(() => {
          setAlert(alertTypes.FAILED_ALL.id)
        })
        .finally(() => {
          setSelectedRowData([])
          setSyncing(false)
        })
    }
  }

  const handleMarkedAsSynced = async () => {
    if (isAdmin) {
      setAlert('SYNC_IN_PROGRESS')
      setSelectedRowData([])
      const payload = buildPayload(selectedRowData)
      await markAsSynced({
        body: payload,
      })
        .unwrap()
        .then(() => {
          setAllowSync(false)
        })
        .catch(() => {
          setAlert(alertTypes.FAILED_ALL.id)
        })
        .finally(() => {
          setSyncing(false)
        })
    }
  }

  useEffect(() => {
    setLoadingGrid(isLoadingItems || isSyncingItems || isSyncing || isLoadingMarkAsSynced)
  }, [isLoadingItems, isSyncingItems, isSyncing, isLoadingMarkAsSynced])

  return (
    <>
      <AlertSnackbar
        message={alertTypes[alert]?.message || ''}
        isOpen={isOpen}
        severity={alertTypes[alert]?.severity || 'success'}
        handleClose={() => setIsOpen(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <LedgerSyncGrid
          rows={transformedRows}
          loading={isLoadingGrid}
          checkboxSelection
          showActionBar
          localeText={{
            noRowsLabel: 'Everything has been synced',
          }}
          actionBarContent={
            <ActionBar
              items={summaryItems}
              erpProvider={erpType}
              isLoading={isSyncingItems || isSyncing}
              allowSync={allowSync}
              onSync={handleSync}
              lastSyncDate={lastSyncDate}
              onMarkAsSynced={handleMarkedAsSynced}
            />
          }
          onSelectionModelChange={handleSelection}
          apiRef={ledgerSyncApiRef}
        />
      </Grid>
    </>
  )
}

export default SyncContainer
